'use client';
import MobileDrawer from '@c/drawers/MobileDrawer';
import CheckboxFilterSection from '@c/filters/sections/CheckboxFilterSection';
import PriceRangeFilterSection from '@c/filters/sections/PriceRangeFilterSection';
import Accordion from '@ui/Accordion';
import Checkbox from '@ui/Checkbox';
import Input from '@ui/Input';
import { getGenderOptions } from '@util/createListingHelpers';
import { useCategoryFilters } from '@util/hooks/useCategoryFilters';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import { getCountryLabelFromCode, isMobile } from '@util/index';
import { CategoryDocument } from 'models/category';
import { FacetCounts } from 'models/product';
import {
  BOOT_SIZES,
  Gender,
  LETTER_SIZES,
  PANT_SIZES,
  RideType,
  rideTypeChoices,
} from 'models/shared';

type MobileSearchFiltersProps = {
  showFilters: boolean;
  setShowFilters: (showFilters: boolean) => void;
  filterState: PageSideFilters;
  setFilterState: (filterState: PageSideFilters) => void;
  currentCat: CategoryDocument | null;
  allBrands: string[];
  facet_counts: FacetCounts | undefined;
  categories: CategoryDocument[];
  showRideTypes: boolean;
};

export default function MobileSearchFilters({
  showFilters,
  setShowFilters,
  filterState,
  setFilterState,
  currentCat,
  allBrands,
  facet_counts,
  categories,
  showRideTypes,
}: MobileSearchFiltersProps) {
  const {
    startAllClosed,
    handleRideTypeChange,
    handleGenderChange,
    getSizeTitle,
    handleNumberSizeChange,
    handleLetterSizeChange,
    getBrandFilterOptions,
    handleBrandsChange,
    brandFilter,
    setBrandFilter,
    handlePricesChange,
    handleSaleChange,
    handleRecentlySoldChange,
    countryCodes,
    handleShipToChange,
    handleShipFromChange,
    onResetFilters,
    handleConditionChange,
    applyPendingFilterChanges,
    pendingFilterChanges,
  } = useCategoryFilters({
    filterState,
    setFilterState,
    currentCat,
    allBrands,
    facet_counts,
    categories,
  });

  return (
    <MobileDrawer
      show={showFilters}
      dismiss={() => setShowFilters(false)}
      title="Filter"
      headerExtraButton={
        <button
          type="button"
          onClick={() => {
            onResetFilters();
            setShowFilters(false);
          }}
          className="text-[2.1rem] font-semibold text-brand-secondary"
        >
          Reset
        </button>
      }
    >
      <div className="max-h-[65rem] overflow-y-auto px-8 pb-[10rem] pt-8">
        <CheckboxFilterSection
          title="Condition"
          filterOptions={['New', 'Used'].map((condition) => ({
            key: condition,
            label: condition,
            value: condition,
          }))}
          onChange={(values) => {
            handleConditionChange(values as ('New' | 'Used')[]);
          }}
          filterState={filterState.conditions ?? []}
          pendingFilters={pendingFilterChanges?.conditions ?? []}
        />
        <div className="mt-4 border-b border-zinc-300">
          <Accordion
            title="Show Only"
            titleClassName="!text-[2.1rem] sm:!text-[1.8rem] !font-semibold"
            startOpen={!startAllClosed}
          >
            <div className="flex flex-col gap-4 p-4">
              <Checkbox
                label="On Sale"
                selected={
                  isMobile() ? pendingFilterChanges?.onsale : filterState.onsale
                }
                onChange={(checked) => handleSaleChange(!!checked)}
              />
              <Checkbox
                label="Sold Items"
                selected={
                  isMobile()
                    ? pendingFilterChanges?.recentlySold
                    : filterState.recentlySold
                }
                onChange={(checked) => handleRecentlySoldChange(!!checked)}
              />
            </div>
          </Accordion>
        </div>

        {showRideTypes && (
          <CheckboxFilterSection
            title="Ride Types"
            startOpen={!startAllClosed}
            filterOptions={rideTypeChoices.map((rideType) => ({
              value: rideType,
              key: rideType,
              label: rideType,
            }))}
            onChange={(values) => handleRideTypeChange(values as RideType[])}
            filterState={filterState.rideTypes ?? []}
            pendingFilters={pendingFilterChanges?.rideTypes ?? []}
          />
        )}

        {currentCat?.product_cats.category !== 'Bike Parts' && (
          <CheckboxFilterSection
            title="Gender / Age"
            startOpen={!startAllClosed}
            filterOptions={getGenderOptions().map((g) => ({
              value: g.value,
              key: g.value,
              label: g.label,
            }))}
            onChange={(values) => handleGenderChange(values)}
            filterState={filterState.genders ?? []}
            pendingFilters={pendingFilterChanges?.genders ?? []}
          />
        )}

        {currentCat?.product_cats.category !== 'Bike Parts' && (
          <CheckboxFilterSection
            title={getSizeTitle()}
            filterOptions={LETTER_SIZES.map((s) => ({
              key: s,
              label: s,
            }))}
            onChange={(values) => handleLetterSizeChange(values)}
            startOpen={!startAllClosed}
            filterState={filterState.letterSizes ?? []}
            pendingFilters={pendingFilterChanges?.letterSizes ?? []}
            facet_counts={facet_counts?.['variations.size']}
          />
        )}

        {currentCat?.product_cats.category1 === 'Boots' && (
          <CheckboxFilterSection
            title="Boot Size"
            filterOptions={BOOT_SIZES[
              (filterState.genders?.length === 1
                ? filterState.genders[0]
                : 'U') as Gender
            ].map((g) => ({ key: g + '', label: g + '' }))}
            onChange={handleNumberSizeChange}
            filterState={filterState.numberSizes ?? []}
            pendingFilters={pendingFilterChanges?.numberSizes ?? []}
            startOpen={!startAllClosed}
          />
        )}

        {currentCat?.product_cats.category1 !== 'Gear' && (
          <CheckboxFilterSection
            title="Pant Size"
            filterOptions={PANT_SIZES[
              (filterState.genders?.length === 1
                ? filterState.genders[0]
                : 'U') as Gender
            ].map((g) => ({ key: g + '', label: g + '' }))}
            onChange={handleNumberSizeChange}
            filterState={filterState.numberSizes ?? []}
            pendingFilters={pendingFilterChanges?.numberSizes ?? []}
            startOpen={!startAllClosed}
          />
        )}

        <CheckboxFilterSection
          title="Brands"
          filterOptions={getBrandFilterOptions()}
          onChange={handleBrandsChange}
          filterState={filterState.brands ?? []}
          pendingFilters={pendingFilterChanges?.brands ?? []}
          startOpen={!startAllClosed}
          facet_counts={facet_counts?.brand}
          noFiltersMessage="No brands found"
          noFiltersClassName="py-8"
        >
          <div className="mb-4 p-4">
            <Input
              placeholder={'Search Brands'}
              type={'search'}
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="!h-[5rem] !rounded-[1.5rem]"
              searchIconRight
            />
          </div>
        </CheckboxFilterSection>

        <div className="mt-4 border-b border-zinc-300">
          <PriceRangeFilterSection
            min={filterState.minPrice}
            max={filterState.maxPrice}
            pendingMin={pendingFilterChanges?.minPrice}
            pendingMax={pendingFilterChanges?.maxPrice}
            title="Price"
            titleClassName="!text-[2.1rem] sm:!text-[1.8rem] !font-semibold"
            onChange={(min, max) => handlePricesChange(min, max)}
            startOpen={
              !startAllClosed ||
              !!filterState.minPrice ||
              !!filterState.maxPrice
            }
          />
        </div>

        {/* on sale */}

        {/* sold items */}

        <CheckboxFilterSection
          startOpen={!startAllClosed}
          title="Ships To"
          filterOptions={countryCodes.map((code) => ({
            key: code,
            value: code,
            label: getCountryLabelFromCode(code),
          }))}
          onChange={(values) => handleShipToChange(values)}
          filterState={filterState.shipTo ?? []}
          pendingFilters={pendingFilterChanges?.shipTo ?? []}
        />

        <CheckboxFilterSection
          startOpen={!startAllClosed}
          title="Ships From"
          filterOptions={countryCodes.map((code) => ({
            key: code,
            value: code,
            label: getCountryLabelFromCode(code),
          }))}
          onChange={(values) => handleShipFromChange(values)}
          filterState={filterState.shipFrom ?? []}
          pendingFilters={pendingFilterChanges?.shipFrom ?? []}
          hideBottomBorder
        />
      </div>

      <div className="absolute bottom-0 w-full bg-white p-6">
        <button
          type="button"
          className="w-full rounded-[2rem] bg-brand-secondary p-6 text-[2.1rem] font-semibold text-white"
          onClick={() => {
            applyPendingFilterChanges();
            setShowFilters(false);
          }}
        >
          Show Results
        </button>
      </div>
    </MobileDrawer>
  );
}
